import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Alert,
  AppBar,
  Autocomplete,
  Box,
  Button,
  Container,
  IconButton,
  Paper,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ThemeModeToggle from '../components/ThemeModeToggle';
import LoadingLockScreen from '../components/LoadingLockScreen';
import LiveHomepageThumbnail from '../components/LiveHomepageThumbnail';
import { auth, db } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';

function normalizeUrl(raw) {
  const v = (raw || '').trim();
  if (!v) return '';
  if (/^https?:\/\//i.test(v)) return v;
  return `https://${v}`;
}

function canConfigureApp(app, profile, uid) {
  if (!app || !profile || !uid) return false;
  if (profile.role === 'super_admin') return true;
  if (profile.companyId !== app.companyId) return false;
  if (profile.role === 'company_admin') return true;
  if (profile.role === 'developer' && app.createdBy === uid) return true;
  return false;
}

export default function SiteAppConfigurePage() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = typeof location.state?.returnTo === 'string' ? location.state.returnTo : null;
  const [authReady, setAuthReady] = useState(false);
  const [profile, setProfile] = useState(null);
  const [app, setApp] = useState(null);
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [homepageUrl, setHomepageUrl] = useState('');
  const [customPreviewUrl, setCustomPreviewUrl] = useState('');
  const [stewardUids, setStewardUids] = useState([]);

  const uid = auth.currentUser?.uid;

  const goBack = () => {
    if (returnTo) navigate(returnTo);
    else navigate(-1);
  };

  const afterSave = () => {
    if (returnTo) {
      const dash = returnTo === '/dashboard' || returnTo.startsWith('/dashboard');
      navigate(returnTo, {
        replace: true,
        state: dash ? { activeTab: 'Monitoring' } : undefined,
      });
      return;
    }
    navigate('/dashboard/monitoring', { replace: true, state: { activeTab: 'Monitoring' } });
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/');
        return;
      }
      setAuthReady(true);
    });
    return () => unsub();
  }, [navigate]);

  const load = useCallback(async () => {
    if (!authReady || !appId || !uid) return;
    setLoading(true);
    setError('');
    try {
      const [userSnap, appSnap] = await Promise.all([
        getDoc(doc(db, 'users', uid)),
        getDoc(doc(db, 'companyApps', appId)),
      ]);
      if (!userSnap.exists()) {
        setError('Profile not found.');
        setLoading(false);
        return;
      }
      const p = userSnap.data();
      setProfile(p);
      if (!appSnap.exists()) {
        setError('This site / app was not found.');
        setApp(null);
        setLoading(false);
        return;
      }
      const a = { id: appSnap.id, ...appSnap.data() };
      if (!canConfigureApp(a, p, uid)) {
        setApp(null);
        setError('You do not have permission to configure this monitor.');
        setLoading(false);
        return;
      }
      setApp(a);
      setName(a.name || '');
      setHomepageUrl(a.homepageUrl || '');
      setCustomPreviewUrl(a.previewImageUrl || '');
      setStewardUids(Array.isArray(a.stewardUserIds) ? a.stewardUserIds : []);

      const companyId = a.companyId;
      if (companyId) {
        const uq = query(collection(db, 'users'), where('companyId', '==', companyId));
        const us = await getDocs(uq);
        const rows = us.docs
          .map((d) => ({ uid: d.id, ...d.data() }))
          .filter((u) => u.role === 'company_admin' || u.role === 'developer');
        rows.sort((x, y) => (x.displayName || x.email || '').localeCompare(y.displayName || y.email || ''));
        setEligibleUsers(rows);
      } else {
        setEligibleUsers([]);
      }
    } catch (e) {
      console.error('SiteAppConfigurePage load:', e);
      setError(e.message || 'Could not load.');
    } finally {
      setLoading(false);
    }
  }, [authReady, appId, uid]);

  useEffect(() => {
    load();
  }, [load]);

  const stewardValue = useMemo(
    () => eligibleUsers.filter((u) => stewardUids.includes(u.uid)),
    [eligibleUsers, stewardUids],
  );

  const handleSave = async () => {
    if (!appId || !app || !canConfigureApp(app, profile, uid)) return;
    const n = name.trim();
    const hp = normalizeUrl(homepageUrl);
    if (!n || !hp) {
      setError('Name and homepage URL are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await updateDoc(doc(db, 'companyApps', appId), {
        name: n,
        homepageUrl: hp,
        previewImageUrl: customPreviewUrl.trim() || '',
        stewardUserIds: stewardUids,
        updatedAt: serverTimestamp(),
        updatedBy: uid,
      });
      afterSave();
    } catch (e) {
      console.error('save site app:', e);
      setError(e.message || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  if (!authReady) {
    return <LoadingLockScreen open />;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="sticky" color="inherit" elevation={1}>
        <Toolbar>
          <IconButton edge="start" onClick={goBack} aria-label="Back">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, ml: 1 }} noWrap>
            Configure site / app
          </Typography>
          <ThemeModeToggle />
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 3 }}>
        {loading && <LoadingLockScreen open />}
        {!loading && error && !app && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}
        {!loading && app && (
          <>
            {error && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Update how this monitor appears and who is listed as a steward. The preview uses a live capture of the
              homepage when possible (third-party services; some sites block it).
            </Typography>

            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Live preview
              </Typography>
              <LiveHomepageThumbnail
                homepageUrl={homepageUrl}
                customImageUrl={customPreviewUrl}
                height={260}
              />
            </Paper>

            <Stack spacing={2} component={Paper} variant="outlined" sx={{ p: 2 }}>
              <TextField label="Display name" value={name} onChange={(e) => setName(e.target.value)} fullWidth required />
              <TextField
                label="Homepage URL"
                value={homepageUrl}
                onChange={(e) => setHomepageUrl(e.target.value)}
                fullWidth
                required
                helperText="Used for liveness checks and for the screenshot preview."
              />
              <TextField
                label="Custom preview image URL (optional)"
                value={customPreviewUrl}
                onChange={(e) => setCustomPreviewUrl(e.target.value)}
                fullWidth
                helperText="If set, overrides automatic homepage capture."
              />
              <Autocomplete
                multiple
                options={eligibleUsers}
                disableCloseOnSelect
                value={stewardValue}
                onChange={(_, v) => setStewardUids(v.map((x) => x.uid))}
                getOptionLabel={(o) =>
                  `${o.displayName || o.email || o.uid} (${(o.role || '').replace(/_/g, ' ')})`
                }
                isOptionEqualToValue={(a, b) => a.uid === b.uid}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="App stewards (admins & developers)"
                    helperText="People responsible for this app monitor. Company admins may edit any monitor in their org."
                  />
                )}
              />
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button onClick={goBack} disabled={saving}>Cancel</Button>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </Stack>
            </Stack>
          </>
        )}
      </Container>
    </Box>
  );
}
